import { useQuery, useMutation } from "@apollo/client/react"
import LoadingButton from "@mui/lab/LoadingButton"
import Avatar from "@mui/material/Avatar"
import TextField from "@mui/material/TextField"
import { Check } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useState } from "react"

import ActivitySkeleton from "@/components/activities/skeleton"
import { CREATE_RESERVATION_NOTE_REFETCH_QUERIES } from "@/graphql/dependencies"
import { CreateReservationNoteDocument, GetReservationNotesDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import tabbAvatar from "@/shared/avatar.svg"
import { useCursorPagination, useFlagWithDelay } from "@/shared/hooks"
import { showErrorToast } from "@/shared/toast"
import { useStore } from "@/store"

const Note = ({ reservation: _reservation, note }) => {
  const defaultAvatar = "https://www.gravatar.com/avatar/?d=mp"

  const avatar = note.avatarUrl || (note.source === "system" ? tabbAvatar : defaultAvatar)
  const createdAt = DateTime.fromISO(note.createdAt).toLocaleString(DateTime.DATETIME_MED)

  return (
    <div className="flex flex-1 items-center space-x-4 px-6 py-2">
      <Avatar className="" src={avatar} />

      <div className="flex-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500 uppercase">{note.name}</span>
          <span className="text-xs text-gray-400">{createdAt}</span>
        </div>
        <div className="text-gray-800 py-1">{note.note}</div>
      </div>
    </div>
  )
}

const NoteForm = ({ reservation }) => {
  const [createNote, { loading }] = useMutation(CreateReservationNoteDocument, {
    refetchQueries: CREATE_RESERVATION_NOTE_REFETCH_QUERIES,
  })
  const { currentUser } = useStore()

  const [note, setNote] = useState("")
  const [success, setSuccess] = useFlagWithDelay()

  const handleChange = (event) => {
    setNote(event.target.value)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    try {
      await createNote({
        variables: {
          reservationId: reservation.id,
          note,
        },
      })

      setNote("")
      setSuccess(2000)
    } catch (error) {
      showErrorToast("Failed to save note. Please try again.")
      console.error(error)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex items-center space-x-4 p-6"
      data-testid="panel-reservations-edit-form-notes-form"
    >
      <Avatar src={currentUser.avatarUrl} />
      <TextField
        className="flex-1"
        multiline
        placeholder={i18n.t("app.reservation_note.add_form.placeholder")}
        value={note}
        onChange={handleChange}
        maxRows={4}
        minRows={1}
      />
      <LoadingButton color={success ? "success" : "primary"} loadingPosition="center" loading={loading} type="submit">
        {success ? <Check /> : i18n.t("app.reservation_note.add_form.submit")}
      </LoadingButton>
    </form>
  )
}

const NotesTab = ({ venue, reservation }) => {
  const { cursor, createNavigationFunctions } = useCursorPagination({ perPage: 5 })

  const { data, loading, error } = useQuery(GetReservationNotesDocument, {
    variables: {
      slug: venue.slug,
      reservationId: reservation.id,
      ...cursor,
    },
  })

  const pageInfo = data?.reservation?.notes?.pageInfo
  const {
    hasPreviousPage: _hasPreviousPage,
    hasNextPage: _hasNextPage,
    previousPage: _previousPage,
    nextPage: _nextPage,
  } = createNavigationFunctions(pageInfo)

  if (!data && loading) {
    return (
      <div className="flex flex-col gap-4 p-2">
        <ActivitySkeleton />
      </div>
    )
  }

  if (error) return <div>Error: {error.message}</div>

  const notes = data?.reservation?.notes?.nodes || []

  const COMPONENTS = {
    customer: Note,
    staff: Note,
    system: Note,
  }

  return (
    <div className="flex flex-col h-full" data-testid="panel-reservations-edit-form-notes">
      <div className="flex-1 overflow-y-auto">
        {notes.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            {i18n.t("app.edit_reservation.no_comments")}
          </div>
        ) : (
          <div className="py-4">
            {notes.map((note) => {
              const Component = COMPONENTS[note.source] || (() => null)
              return <Component key={note.id} reservation={reservation} note={note} />
            })}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 border-t border-gray-200">
        <NoteForm reservation={reservation} />
      </div>
    </div>
  )
}
export default NotesTab
